<template>
    <div class="flex flex-col lg:flex-row min-h-screen">
        <aside
            class="w-full border-b border-theme-border bg-theme-card lg:sticky lg:top-0 lg:h-screen lg:w-80 lg:flex-shrink-0 lg:border-b-0 lg:border-r lg:overflow-y-auto"
        >
            <!-- Navegación -->
            <div class="border-b border-theme-border px-5 py-4">
                <button
                    type="button"
                    class="flex items-center gap-2 text-sm font-medium text-theme-muted transition hover:text-theme-primary"
                    @click="goToHome"
                >
                    <chevron-left-icon class="h-4 w-4" />
                    Volver al panel
                </button>
            </div>

            <!-- Información -->
            <div class="px-4 py-5 md:mb-8">
                <div class="flex items-start gap-4">
                    <div class="flex-1">
                        <h2 class="text-xl font-semibold text-theme-base">
                            Editor de Catálogo 🧩
                        </h2>

                        <p class="mt-2 text-sm leading-6 text-theme-muted">
                            Haz click directamente sobre el nombre, la
                            descripción, el logo, las redes o el WhatsApp del
                            preview para editarlos. Otras configuraciones están
                            disponibles en "Perfil" y "Diseño".
                        </p>
                    </div>
                </div>
                <catalog-editor-quick-actions-toolbar
                    class="mt-5"
                    :actions="quickActions"
                />

                <div
                    class="mt-3 flex w-full items-center rounded-lg border border-theme-border p-1"
                >
                    <button
                        type="button"
                        class="flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition"
                        :class="
                            previewDevice === 'mobile'
                                ? 'bg-theme-primary text-theme-inverse'
                                : 'text-theme-muted hover:bg-theme-hover'
                        "
                        @click="previewDevice = 'mobile'"
                    >
                        📱 Celular
                    </button>
                    <button
                        type="button"
                        class="flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition"
                        :class="
                            previewDevice === 'desktop'
                                ? 'bg-theme-primary text-theme-inverse'
                                : 'text-theme-muted hover:bg-theme-hover'
                        "
                        @click="previewDevice = 'desktop'"
                    >
                        🖥️ Escritorio
                    </button>
                </div>

                <button
                    v-if="catalog"
                    type="button"
                    class="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-theme-primary px-3 py-2 text-sm font-semibold text-theme-primary transition hover:bg-theme-hover"
                    @click="previewModalOpen = true"
                >
                    <eye-icon class="h-4 w-4" />
                    Ver como queda
                </button>

                <display-mode-switch
                    v-if="catalog"
                    class="mt-3"
                    :display-mode="catalog.displayMode"
                    :segments-count="draft.segments.length"
                    @updated="onDisplayModeUpdated"
                />

                <div
                    v-if="hasPendingChanges"
                    class="mt-3 flex w-full items-center gap-2"
                >
                    <app-button
                        class="flex-1"
                        color="success"
                        :loading="isSavingDraft"
                        @click="saveDraftChanges"
                    >
                        Guardar cambios
                    </app-button>
                    <button
                        type="button"
                        :disabled="isSavingDraft"
                        class="rounded-lg border border-theme-border bg-theme-card px-3 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover disabled:opacity-50"
                        @click="discardDraftChanges"
                    >
                        Descartar
                    </button>
                </div>

                <a
                    v-if="$publicUrl"
                    :href="$publicUrl"
                    target="_blank"
                    rel="noopener"
                    :title="$publicUrl"
                    class="mt-3 md:hidden flex w-full items-center gap-1.5 rounded-lg border border-theme-border px-3 py-2 text-xs text-theme-muted no-underline transition hover:bg-theme-hover hover:text-theme-base"
                >
                    <span class="relative flex h-2 w-2 flex-shrink-0">
                        <span
                            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75"
                        ></span>
                        <span
                            class="relative inline-flex rounded-full h-2 w-2 bg-theme-success"
                        ></span>
                    </span>
                    <span class="truncate">{{ catalogLabel }}</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="w-3 h-3 flex-shrink-0 opacity-60"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.75a.75.75 0 01.75-.75h3.25a.75.75 0 01.75.75v3.25a.75.75 0 01-1.5 0v-1.44l-4.72 4.72a.75.75 0 11-1.06-1.06l4.72-4.72h-1.44a.75.75 0 01-.75-.75z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </a>

                <tucatalogo-footer-card
                    v-if="catalog && showWatermarkCard"
                    class="mt-6"
                    :value="catalog.showWatermark"
                    :locked="!canUpdateWatermark"
                    :loading="isSavingWatermark"
                    @input="onWatermarkToggle"
                    @upgrade="openWatermarkUpgrade"
                />
            </div>
        </aside>

        <catalog-history-modal
            v-model="historyModalOpen"
            :catalog="catalog"
            @restored="loadCatalog"
        />

        <main
            class="flex-1 flex flex-col items-center px-5 pt-5 font-sans md:px-10 md:pt-10 relative lg:overflow-y-auto lg:h-screen bg-stack-page-bg"
            :style="{
                '--color-stack-primary': stack.primary,
                '--color-stack-emphasis': stack.emphasis,
                '--color-stack-text': stack.text,
                '--color-stack-background': stack.background,
                '--color-stack-ink': ink,
                '--color-stack-page-bg': pageBackground
            }"
        >
            <AppDialog v-model="previewModalOpen">
                <template #header>
                    <div class="border-b border-theme-border pb-4">
                        <h2 class="text-lg font-semibold text-theme-base">
                            Así se verá tu catálogo
                        </h2>
                        <p class="mt-1 text-sm text-theme-muted">
                            📱 Vista de celular, como la ven la mayoría de tus
                            clientes.
                        </p>
                        <a
                            v-if="$publicUrl"
                            :href="$publicUrl"
                            target="_blank"
                            rel="noopener"
                            :title="$publicUrl"
                            class="mt-3 flex w-full items-center gap-2 rounded-lg border border-theme-border px-3 py-2 text-sm font-medium text-theme-base no-underline transition hover:border-theme-primary hover:text-theme-primary"
                        >
                            <span class="relative flex h-2 w-2 flex-shrink-0">
                                <span
                                    class="animate-ping absolute inline-flex h-full w-full rounded-full bg-theme-success opacity-75"
                                ></span>
                                <span
                                    class="relative inline-flex h-2 w-2 rounded-full bg-theme-success"
                                ></span>
                            </span>
                            <span class="min-w-0 flex-1 truncate">
                                {{ catalogLabel }}
                            </span>
                            <external-link-icon
                                class="h-4 w-4 flex-shrink-0 opacity-60"
                            />
                        </a>
                    </div>
                </template>
                <div class="flex justify-center pt-4">
                    <catalog-preview-frame
                        :catalog="previewCatalog"
                        class="max-w-md overflow-hidden rounded-2xl shadow-lg"
                    />
                </div>
            </AppDialog>

            <AppDialog
                v-model="themeModalOpen"
                size="lg"
                @close="resetThemeForm"
            >
                <template #header>
                    <h2 class="text-xl font-semibold text-theme-base">
                        Colores
                    </h2>
                </template>

                <p class="text-sm text-theme-muted mb-4">
                    Ajusta los colores que representan tu negocio. El estilo de
                    cada bloque se elige en el bloque mismo.
                </p>

                <brand-color-picker
                    v-model="themeForm.stackColors"
                    :business-name="displayName"
                    :logo-url="logoIconUrl"
                    :display-mode="catalog && catalog.displayMode"
                    default-mode="custom"
                    more-palettes="all"
                />

                <template #footer>
                    <div class="flex justify-end gap-2">
                        <button
                            class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                            @click="themeModalOpen = false"
                        >
                            Cancelar
                        </button>

                        <app-button
                            :loading="isThemeSaving"
                            color="success"
                            @click="updateTheme"
                        >
                            Guardar
                        </app-button>
                    </div>
                </template>
            </AppDialog>
            <div
                v-if="catalog"
                class="w-full rounded-t-3xl shadow-xl px-5 pt-10 pb-28 md:px-8 md:pt-10 md:pb-24 bg-stack-background"
                :class="previewDevice === 'desktop' ? 'max-w-5xl' : 'max-w-md'"
            >
                <!-- Header -->
                <div
                    class="flex items-center"
                    :class="
                        previewDevice === 'desktop'
                            ? 'flex-row text-left space-x-10'
                            : 'flex-col text-center'
                    "
                >
                    <url-image-manager
                        :current-url="logoIconUrl"
                        default-alt="Logo del negocio"
                        :allow-edit-alt="false"
                        default-size="customImage"
                        :preview-width="130"
                        :preview-height="130"
                        :hover-mode="true"
                        @updated="onLogoIconUpdated"
                    />

                    <div class="mt-4 md:mt-0">
                        <div
                            class="group relative flex items-center gap-2"
                            :class="
                                previewDevice === 'desktop'
                                    ? 'justify-start'
                                    : 'justify-center'
                            "
                        >
                            <h1
                                class="cursor-pointer text-2xl font-semibold pl-3 md:text-4xl text-stack-primary"
                                @click="startEditingName"
                            >
                                {{ displayName }}
                            </h1>

                            <button
                                type="button"
                                class="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-theme-border bg-theme-card px-3.5 py-2 text-xs font-semibold text-theme-muted shadow-sm transition-all group-hover:border-theme-primary group-hover:bg-theme-primary group-hover:text-white group-hover:shadow hover:scale-105 active:scale-95"
                                aria-label="Editar nombre del catálogo"
                                @click="startEditingName"
                            >
                                <svg
                                    class="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    stroke-width="2"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        d="M15.232 5.232l3.536 3.536M9 17l-4 1 1-4 9.293-9.293a1 1 0 011.414 0l2.586 2.586a1 1 0 010 1.414L9 17z"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div class="mt-2 max-w-md">
                            <div
                                class="group relative flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-transparent p-3 transition-all"
                            >
                                <p
                                    class="flex-1 cursor-pointer text-sm md:text-base leading-relaxed text-stack-text"
                                    @click="startEditing"
                                >
                                    {{
                                        businessDescription ||
                                        "Haz clic aquí para agregar una descripción de tu negocio..."
                                    }}
                                </p>

                                <button
                                    type="button"
                                    class="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-theme-border bg-theme-card px-3.5 py-2 text-xs font-semibold text-theme-muted shadow-sm transition-all group-hover:border-theme-primary group-hover:bg-theme-primary group-hover:text-white group-hover:shadow hover:scale-105 active:scale-95"
                                    aria-label="Editar descripción del negocio"
                                    @click="startEditing"
                                >
                                    <svg
                                        class="h-4 w-4 shrink-0 transition-transform group-hover:rotate-12"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        stroke-width="2"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M15.232 5.232l3.536 3.536M9 17l-4 1 1-4 9.293-9.293a1 1 0 011.414 0l2.586 2.586a1 1 0 010 1.414L9 17z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <AppDialog
                    v-model="nameModalOpen"
                    size="lg"
                    @close="resetNameForm"
                >
                    <template #header>
                        <h2 class="text-lg font-semibold text-theme-base">
                            Nombre del catálogo
                        </h2>
                    </template>

                    <input
                        ref="nameInput"
                        v-model="nameDraft"
                        type="text"
                        maxlength="80"
                        class="w-full rounded-lg border border-theme-border bg-theme-input p-3 text-sm text-theme-base focus:border-theme-primary focus:outline-none"
                    />

                    <template #footer>
                        <div class="flex justify-end gap-2">
                            <button
                                class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                                @click="nameModalOpen = false"
                            >
                                Cancelar
                            </button>

                            <app-button
                                color="success"
                                @click="finishEditingName"
                            >
                                Guardar
                            </app-button>
                        </div>
                    </template>
                </AppDialog>

                <AppDialog
                    v-model="descriptionModalOpen"
                    size="lg"
                    @close="resetDescriptionForm"
                >
                    <template #header>
                        <h2 class="text-lg font-semibold text-theme-base">
                            Descripción del negocio
                        </h2>
                    </template>

                    <textarea
                        ref="descriptionInput"
                        v-model="descriptionDraft"
                        rows="5"
                        class="w-full rounded-lg border border-theme-border bg-theme-input p-3 text-sm text-theme-base focus:border-theme-primary focus:outline-none"
                    ></textarea>

                    <template #footer>
                        <div class="flex justify-end gap-2">
                            <button
                                class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                                @click="cancelEditing"
                            >
                                Cancelar
                            </button>

                            <app-button color="success" @click="finishEditing">
                                Guardar
                            </app-button>
                        </div>
                    </template>
                </AppDialog>

                <!-- Social -->
                <div
                    class="flex flex-wrap items-center justify-center gap-6 py-6"
                >
                    <a
                        v-for="social in socialEntries"
                        :key="social.network"
                        :href="social.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="transition duration-200 hover:opacity-75 text-stack-emphasis"
                    >
                        <SocialIcon
                            :network="social.network"
                            :height="10"
                            :width="10"
                        />
                    </a>

                    <button
                        type="button"
                        :aria-label="
                            socialEntries.length
                                ? 'Editar redes sociales'
                                : 'Agregar redes sociales'
                        "
                        class="flex items-center justify-center rounded-full border border-theme-border bg-theme-card w-8 h-8 text-theme-muted transition hover:border-theme-primary hover:bg-theme-primary hover:text-white"
                        @click="openSocialModal"
                    >
                        <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                :d="
                                    socialEntries.length
                                        ? 'M15.232 5.232l3.536 3.536M9 17l-4 1 1-4 9.293-9.293a1 1 0 011.414 0l2.586 2.586a1 1 0 010 1.414L9 17z'
                                        : 'M12 4v16m8-8H4'
                                "
                            />
                        </svg>
                    </button>
                </div>

                <AppDialog
                    v-model="socialModalOpen"
                    size="lg"
                    @close="resetSocialForm"
                >
                    <template #header>
                        <h2 class="text-lg font-semibold text-theme-base">
                            Redes sociales
                        </h2>
                    </template>

                    <social-networks-editor
                        v-model="newSocialNetworksList"
                        auto-open-when-empty
                    />

                    <template #footer>
                        <div class="flex justify-end gap-2">
                            <button
                                class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                                @click="socialModalOpen = false"
                            >
                                Cancelar
                            </button>

                            <button
                                class="rounded-lg bg-theme-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                                @click="saveSocialNetworks"
                            >
                                Guardar
                            </button>
                        </div>
                    </template>
                </AppDialog>

                <AppDialog
                    v-model="linkModalOpen"
                    size="lg"
                    @close="resetLinkForm"
                >
                    <template #header>
                        <h2 class="text-lg font-semibold text-theme-base">
                            Nuevo enlace
                        </h2>
                    </template>

                    <div class="space-y-4">
                        <div>
                            <label class="text-sm text-theme-muted block mb-1">
                                Imagen (opcional)
                            </label>
                            <div class="flex flex-col items-center gap-2">
                                <image-manage
                                    v-model="newLinkForm.image"
                                    :enabled-create="true"
                                />
                                <button
                                    v-if="newLinkForm.image"
                                    type="button"
                                    class="text-xs text-theme-muted hover:text-theme-danger"
                                    @click="newLinkForm.image = null"
                                >
                                    Quitar imagen
                                </button>
                            </div>
                        </div>
                        <div>
                            <label class="text-sm text-theme-muted block mb-1">
                                Título *
                            </label>
                            <input
                                v-model="newLinkForm.title"
                                placeholder="Título del enlace"
                                maxlength="120"
                                class="outline-none text-base w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                            />
                            <p
                                v-if="linkModalErrors.title"
                                class="text-theme-danger text-xs mt-1"
                            >
                                {{ linkModalErrors.title }}
                            </p>
                        </div>
                        <div>
                            <label class="text-sm text-theme-muted block mb-1">
                                URL *
                            </label>
                            <input
                                v-model="newLinkForm.url"
                                placeholder="https://..."
                                class="outline-none text-base w-full border border-theme-border rounded-lg px-3 py-2 bg-theme-input text-theme-base"
                            />
                            <p
                                v-if="linkModalErrors.url"
                                class="text-theme-danger text-xs mt-1"
                            >
                                {{ linkModalErrors.url }}
                            </p>
                        </div>
                    </div>

                    <template #footer>
                        <div
                            class="flex flex-col sm:flex-row justify-end gap-2"
                        >
                            <button
                                class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                                @click="linkModalOpen = false"
                            >
                                Cancelar
                            </button>

                            <app-button
                                color="success"
                                :loading="isSavingLink"
                                @click="saveNewLink"
                            >
                                Crear enlace
                            </app-button>
                        </div>
                    </template>
                </AppDialog>

                <create-product-modal
                    v-model="productModalOpen"
                    @created="onProductCreatedFromEntry"
                />

                <div
                    v-if="showMixTabs"
                    class="mx-auto mb-3 mt-2 flex w-full max-w-xs items-center rounded-lg border border-stack-text bg-stack-background p-1"
                >
                    <button
                        type="button"
                        class="flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition"
                        :class="
                            editorTab === 'home'
                                ? 'bg-stack-emphasis text-stack-ink'
                                : 'text-stack-text'
                        "
                        @click="editorTab = 'home'"
                    >
                        Home
                    </button>
                    <button
                        type="button"
                        class="flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition"
                        :class="
                            editorTab === 'store'
                                ? 'bg-stack-emphasis text-stack-ink'
                                : 'text-stack-text'
                        "
                        @click="editorTab = 'store'"
                    >
                        Tienda
                    </button>
                </div>

                <segments-editor
                    v-if="showSegmentsSection"
                    v-model="draft.segments"
                    :stack="stack"
                    :colors="colors"
                    :dark="dark"
                    :is-desktop="previewDevice === 'desktop'"
                    :allowed-types="linksOnlySegments ? ['links'] : null"
                    :is-links-only-mode="linksOnlySegments"
                    class="border-t pt-6 border-stack-text"
                />

                <template v-if="showProductsSection">
                    <!-- Grid title -->
                    <div
                        class="hidden md:flex justify-center items-center border-t gap-2 pb-5 pt-5 text-xs uppercase tracking-widest font-semibold relative border-stack-text text-stack-text"
                    >
                        <div
                            class="flex items-center justify-center gap-2 transition-all duration-500 ease-in-out"
                            :class="
                                searchOpen
                                    ? 'opacity-0 pointer-events-none'
                                    : 'opacity-100'
                            "
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                            >
                                <rect
                                    x="3"
                                    y="3"
                                    width="7"
                                    height="7"
                                    rx="1.5"
                                />
                                <rect
                                    x="14"
                                    y="3"
                                    width="7"
                                    height="7"
                                    rx="1.5"
                                />
                                <rect
                                    x="3"
                                    y="14"
                                    width="7"
                                    height="7"
                                    rx="1.5"
                                />
                                <rect
                                    x="14"
                                    y="14"
                                    width="7"
                                    height="7"
                                    rx="1.5"
                                />
                            </svg>
                            Productos

                            <button
                                v-if="showSearchBar && !productsReorderMode"
                                type="button"
                                class="absolute left-0 flex h-8 w-8 items-center justify-center transition hover:opacity-70"
                                aria-label="Buscar productos"
                                @click="searchOpen = true"
                            >
                                <svg
                                    class="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="M20 20l-3.5-3.5" />
                                </svg>
                            </button>

                            <button
                                v-if="$ability.hasPermission('product:Create')"
                                type="button"
                                class="absolute right-0 inline-flex items-center gap-1.5 rounded-lg bg-theme-success px-4 py-2 normal-case tracking-normal text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                                @click="openProductModal"
                            >
                                <plus-icon class="h-4 w-4" />
                                Crear producto
                            </button>
                        </div>

                        <div
                            v-if="showSearchBar"
                            class="absolute inset-0 flex items-center transition-all duration-500 ease-in-out"
                            :class="
                                searchOpen
                                    ? 'opacity-100'
                                    : 'opacity-0 pointer-events-none'
                            "
                        >
                            <div class="flex w-full items-center gap-3 px-4">
                                <!-- Lupa -->
                                <svg
                                    class="h-5 w-5 shrink-0"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                >
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="M20 20l-3.5-3.5" />
                                </svg>

                                <input
                                    v-model="search"
                                    class="flex-1 text-sm outline-none"
                                    style="--color-theme-input: transparent"
                                    placeholder="Buscar productos..."
                                    autofocus
                                />

                                <button
                                    type="button"
                                    class="text-xs uppercase tracking-wider opacity-70 hover:opacity-100"
                                    @click="
                                        searchOpen = false;
                                        search = '';
                                    "
                                >
                                    Cancelar
                                </button>
                            </div>
                            <div
                                class="absolute bottom-0 left-0 h-px bg-current transition-all duration-500"
                                :class="
                                    searchOpen
                                        ? 'w-full opacity-30'
                                        : 'w-0 opacity-0'
                                "
                            ></div>
                        </div>
                    </div>

                    <!-- Cargando productos -->
                    <div
                        v-if="isLoadingProducts"
                        class="border-t pt-5 border-stack-text"
                    >
                        <div class="grid gap-2 md:gap-4 grid-cols-3">
                            <div
                                v-for="n in 6"
                                :key="n"
                                class="rounded-lg bg-theme-hover animate-pulse"
                                style="padding-top: 100%"
                            ></div>
                        </div>
                    </div>

                    <template v-else>
                        <!-- Empty -->
                        <div
                            v-if="!products.length && !hasMoreProducts"
                            class="border-t text-center py-16 border-stack-text"
                        >
                            <div
                                class="text-xl font-semibold text-stack-primary"
                            >
                                Aún no hay productos publicados
                            </div>

                            <p class="mt-3 text-sm text-stack-text">
                                Cuando agregues tus productos aparecerán aquí.
                            </p>
                        </div>

                        <div v-else class="border-t pt-5 border-stack-text">
                            <div
                                v-if="products.length > 1 && !searchOpen"
                                class="flex justify-end mb-4"
                            >
                                <button
                                    v-if="productsReorderMode"
                                    class="inline-flex items-center gap-1.5 rounded-lg bg-theme-success px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:opacity-90"
                                    @click="toggleProductsReorder"
                                >
                                    <check-icon class="h-4 w-4" />
                                    Listo
                                </button>
                                <button
                                    v-else
                                    class="flex items-center justify-center p-2 gap-1.5 rounded-lg border border-theme-border bg-theme-card px-2 text-theme-muted text-sm font-semibold transition hover:border-theme-primary hover:bg-theme-primary hover:text-white"
                                    @click="toggleProductsReorder"
                                >
                                    <sort-icon class="h-4 w-4" />
                                    Ordenar
                                </button>
                            </div>

                            <!-- Sin resultados de búsqueda -->
                            <div
                                v-if="search.trim() && !visibleProducts.length"
                                class="text-center py-16"
                            >
                                <div
                                    class="text-xl font-semibold text-stack-primary"
                                >
                                    No se encontraron productos
                                </div>

                                <p class="mt-3 text-sm text-stack-text">
                                    Intenta con otro término de búsqueda.
                                </p>
                            </div>

                            <VueDraggable
                                v-else
                                v-model="products"
                                tag="div"
                                handle=".product-drag-handle"
                                :disabled="!productsReorderMode"
                                class="grid gap-2 md:gap-4 grid-cols-3"
                            >
                                <editor-product-card
                                    v-for="p in visibleProducts"
                                    :key="p.slug"
                                    :product="p"
                                    :draggable="productsReorderMode"
                                    :colors="colors"
                                    :dark="dark"
                                    :card-shape-style="cardShapeStyle"
                                    @edit="editProduct"
                                    @toggle-publish="togglePublish"
                                />
                            </VueDraggable>
                        </div>

                        <template v-if="unpublishedProducts.length">
                            <div
                                class="mt-6 mb-3 text-sm font-semibold text-stack-text"
                            >
                                Sin publicar
                            </div>
                            <div class="grid gap-2 md:gap-4 grid-cols-3">
                                <editor-product-card
                                    v-for="p in visibleUnpublishedProducts"
                                    :key="p.slug"
                                    :product="p"
                                    :colors="colors"
                                    :dark="dark"
                                    :card-shape-style="cardShapeStyle"
                                    @edit="editProduct"
                                    @toggle-publish="togglePublish"
                                />
                            </div>
                        </template>

                        <div
                            v-if="
                                hasMoreProducts &&
                                !productsReorderMode &&
                                !search.trim()
                            "
                            class="mt-6 flex justify-center"
                        >
                            <button
                                type="button"
                                class="rounded-lg border border-theme-border bg-theme-card px-4 py-2 text-sm font-medium text-theme-primary transition hover:bg-theme-hover disabled:opacity-60"
                                :disabled="isLoadingMoreProducts"
                                @click="loadMoreProducts"
                            >
                                {{
                                    isLoadingMoreProducts
                                        ? "Cargando..."
                                        : "Cargar más"
                                }}
                            </button>
                        </div>
                    </template>
                </template>

                <catalog-watermark v-if="catalog.showWatermark" />
            </div>

            <div
                v-else
                class="flex w-full max-w-5xl flex-col items-center justify-center gap-3 rounded-2xl bg-brand-gray py-24 text-white"
            >
                <svg
                    class="h-8 w-8 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                </svg>
                <span class="text-sm">Cargando catálogo...</span>
            </div>

            <div class="fixed right-5 bottom-14 md:bottom-12 z-50">
                <a
                    v-if="whatsappBase"
                    :href="whatsappLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="w-14 h-14 md:w-16 md:h-16 rounded-full bg-theme-success flex items-center justify-center shadow-xl text-white"
                >
                    <SocialIcon network="whatsapp" />
                </a>

                <button
                    v-else
                    type="button"
                    class="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-dashed border-theme-border bg-theme-card flex items-center justify-center shadow-xl text-theme-muted transition hover:border-theme-primary hover:text-theme-primary"
                    aria-label="Agregar número de WhatsApp"
                    @click="startEditingWhatsapp"
                >
                    <SocialIcon network="whatsapp" />
                </button>

                <button
                    v-if="whatsappBase"
                    type="button"
                    class="absolute -top-1 -left-1 flex h-6 w-6 items-center justify-center rounded-full border border-theme-border bg-theme-card text-theme-muted shadow-sm transition hover:border-theme-primary hover:bg-theme-primary hover:text-white"
                    aria-label="Editar número de WhatsApp"
                    @click="startEditingWhatsapp"
                >
                    <svg
                        class="h-3 w-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M15.232 5.232l3.536 3.536M9 17l-4 1 1-4 9.293-9.293a1 1 0 011.414 0l2.586 2.586a1 1 0 010 1.414L9 17z"
                        />
                    </svg>
                </button>
            </div>

            <AppDialog
                v-model="whatsappModalOpen"
                size="md"
                @close="resetWhatsappForm"
            >
                <template #header>
                    <h2 class="text-lg font-semibold text-theme-base">
                        Número de WhatsApp
                    </h2>
                </template>

                <WhatsappNumberInput
                    ref="whatsappInput"
                    v-model="whatsappDraft"
                    :country.sync="whatsappDraftCountry"
                    :pinned-country="draftDefaultCountry"
                    :invalid="!isWhatsappDraftValid && whatsappDraftTouched"
                    @blur="whatsappDraftTouched = true"
                />
                <p
                    v-if="!isWhatsappDraftValid && whatsappDraftTouched"
                    class="mt-1 text-xs text-theme-danger"
                >
                    Solo dígitos, sin el indicativo (mínimo 7)
                </p>

                <template #footer>
                    <div class="flex justify-end gap-2">
                        <button
                            class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                            @click="whatsappModalOpen = false"
                        >
                            Cancelar
                        </button>

                        <app-button
                            :disabled="!isWhatsappDraftValid"
                            color="success"
                            @click="finishEditingWhatsapp"
                        >
                            Guardar
                        </app-button>
                    </div>
                </template>
            </AppDialog>
        </main>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import SocialNetworksEditor from "@/pages/app/customization/SocialNetworksEditor.vue";
import VueDraggable from "vuedraggable";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import { MARKETPLACE_PORTAL_URL } from "@/common/constants";
import { LINKS_ONLY_DISPLAY_MODES } from "@/common/fields/display-modes";
import { canShowUpgrade } from "@/common/fields/plan-features";
import { getCardShapeStyle } from "@/common/lib/button-style";
import { alpha, isHexColor, luminance, mix } from "@/common/lib/color";
import { applyDrag } from "@/common/lib/drag";
import { isPlanLimitError } from "@/common/lib/formatter";
import { stripUrlProtocol } from "@/common/lib/url";
import {
    DEFAULT_WHATSAPP_COUNTRY,
    buildWhatsappNumber,
    buildWhatsappWaMeLink,
    isValidWhatsappLocalNumber,
    normalizeWhatsappCountryCode,
    splitWhatsappNumber
} from "@/common/lib/whatsapp";

import CatalogWatermark from "@/components/catalog/CatalogWatermark.vue";
import BrandColorPicker from "@/components/editor/BrandColorPicker.vue";
import CatalogEditorQuickActionsToolbar from "@/components/editor/CatalogEditorQuickActionsToolbar.vue";
import CatalogHistoryModal from "@/components/editor/CatalogHistoryModal.vue";
import CatalogPreviewFrame from "@/components/editor/CatalogPreviewFrame.vue";
import CreateProductModal from "@/components/editor/CreateProductModal.vue";
import DisplayModeSwitch from "@/components/editor/DisplayModeSwitch.vue";
import EditorProductCard from "@/components/editor/EditorProductCard.vue";
import SegmentsEditor from "@/components/editor/SegmentsEditor.vue";
import TucatalogoFooterCard from "@/components/editor/TucatalogoFooterCard.vue";
import CheckIcon from "@/components/icons/CheckIcon.vue";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon.vue";
import ClockIcon from "@/components/icons/ClockIcon.vue";
import ExternalLinkIcon from "@/components/icons/ExternalLinkIcon.vue";
import EyeIcon from "@/components/icons/EyeIcon.vue";
import PaletteIcon from "@/components/icons/PaletteIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import SocialIcon from "@/components/icons/SocialIcon.vue";
import SortIcon from "@/components/icons/SortIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import ImageManage from "@/components/utils/ImageManage.vue";
import UrlImageManager from "@/components/utils/UrlImageManager.vue";
import WhatsappNumberInput from "@/components/utils/WhatsappNumberInput.vue";

// Forma fija de las cards del preview: ya no se deriva de un tema.
const DEFAULT_CARD_SHAPE = "rounded";

const DEFAULT_STACK = {
    primary: "#284D6F",
    emphasis: "#FD951E",
    text: "#000000",
    background: "#FFFFFF"
};

const MAX_CUSTOM_LINKS = 20;

// Mismo tamaño de página que ProductsSearch/CatalogPreviewCard.
const PRODUCTS_PAGE_SIZE = 12;

const SOCIAL_LABELS = {
    tiktok: "TikTok",
    youtube: "YouTube",
    twitter: "X",
    x: "X",
    linkedin: "LinkedIn",
    whatsapp: "WhatsApp"
};

export default {
    name: "CatalogPreviewPage",
    components: {
        SocialIcon,
        ChevronLeftIcon,
        CheckIcon,
        EyeIcon,
        ExternalLinkIcon,
        SortIcon,
        PlusIcon,
        CatalogHistoryModal,
        CatalogEditorQuickActionsToolbar,
        CatalogPreviewFrame,
        CatalogWatermark,
        CreateProductModal,
        BrandColorPicker,
        DisplayModeSwitch,
        EditorProductCard,
        ImageManage,
        SegmentsEditor,
        TucatalogoFooterCard,
        UrlImageManager,
        AppDialog,
        SocialNetworksEditor,
        VueDraggable,
        WhatsappNumberInput
    },
    mixins: [planLimitError],
    beforeRouteLeave(to, from, next) {
        if (!this.hasPendingChanges) {
            next();
            return;
        }
        this.$swal
            .fire({
                background: this.backgroundColor,
                title: "Tienes cambios sin guardar",
                text: "¿Qué quieres hacer antes de salir?",
                icon: "warning",
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: "Guardar y salir",
                denyButtonText: "Salir sin guardar",
                cancelButtonText: "Seguir editando"
            })
            .then(async (result) => {
                if (result.isConfirmed) {
                    await this.saveDraftChanges();
                    next(!this.hasPendingChanges);
                    return;
                }
                next(result.isDenied);
            });
    },
    layout: "app",
    data() {
        return {
            catalog: null,
            draft: null,
            baseline: null,
            editorTab: "home",
            isSavingDraft: false,
            isLoadingProducts: false,
            isLoadingMoreProducts: false,
            productsPage: 1,
            hasMoreProducts: false,
            productsReorderMode: false,
            search: "",
            searchOpen: false,
            previewDevice: "mobile",
            nameModalOpen: false,
            nameDraft: "",
            descriptionModalOpen: false,
            descriptionDraft: "",
            whatsappModalOpen: false,
            whatsappDraft: "",
            whatsappDraftCountry: DEFAULT_WHATSAPP_COUNTRY,
            whatsappDraftTouched: false,
            socialModalOpen: false,
            newSocialNetworksList: [],
            linksReorderMode: false,
            reorderedLinks: [],
            linkModalOpen: false,
            newLinkForm: { title: "", url: "", image: null },
            linkModalErrors: {},
            isSavingLink: false,
            productModalOpen: false,
            themeModalOpen: false,
            previewModalOpen: false,
            historyModalOpen: false,
            isThemeSaving: false,
            isSavingWatermark: false,
            themeForm: {
                stackColors: { ...DEFAULT_STACK }
            }
        };
    },
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        ...mapState("auth", ["tenantName", "tenantSlug", "permissions"]),
        canUpdateWatermark() {
            return this.$ability.hasPermission(
                "tenant:UpdateWatermarkSettings"
            );
        },
        showWatermarkCard() {
            return (
                this.canUpdateWatermark ||
                canShowUpgrade(this.permissions, "catalogHideWatermark")
            );
        },
        logoIconUrl() {
            return this.draft?.logoIconUrl ?? "";
        },
        businessDescription() {
            return this.draft?.businessDescription ?? "";
        },
        socialNetworks() {
            return this.draft?.socialNetworks ?? [];
        },
        stackColors() {
            return this.catalog?.stackColors ?? {};
        },
        // Catálogo del draft actual (sin guardar), con la forma que espera
        // CatalogPreviewFrame/buildCatalogPreviewViewModel. stackColors y
        // displayMode no viven en el draft (se guardan aparte, ver
        // updateTheme/onDisplayModeUpdated), así que se toman de
        // this.catalog, que sí los refleja en vivo.
        previewCatalog() {
            return {
                ...this.draft,
                stackColors: this.catalog?.stackColors,
                displayMode: this.catalog?.displayMode,
                showWatermark: this.catalog?.showWatermark
            };
        },
        whatsappNumber() {
            return this.draft?.whatsappNumber ?? "";
        },
        draftDefaultCountry() {
            return this.draft?.defaultCountry ?? DEFAULT_WHATSAPP_COUNTRY;
        },
        isWhatsappDraftValid() {
            return isValidWhatsappLocalNumber(
                this.whatsappDraftCountry,
                this.whatsappDraft
            );
        },
        catalogLabel() {
            return stripUrlProtocol(this.$publicUrl);
        },
        showSearchBar() {
            return Boolean(this.draft?.showSearchBar);
        },
        hasProductsDisplayMode() {
            const mode = this.catalog?.displayMode;
            return mode === "catalog" || mode === "mix";
        },
        showProductsSection() {
            const mode = this.catalog?.displayMode;
            return (
                mode === "catalog" ||
                (mode === "mix" && this.editorTab === "store")
            );
        },
        showSegmentsSection() {
            const mode = this.catalog?.displayMode;
            return (
                mode === "segments" ||
                mode === "feed" ||
                mode === "catalog" ||
                (mode === "mix" && this.editorTab === "home")
            );
        },
        linksOnlySegments() {
            const mode = this.catalog?.displayMode;
            return mode === "feed" || mode === "catalog";
        },
        showMixTabs() {
            return this.catalog?.displayMode === "mix";
        },
        quickActions() {
            const actions = [
                {
                    key: "colors",
                    icon: PaletteIcon,
                    label: "Personalizar colores",
                    onClick: this.openThemeModal
                }
            ];

            if (
                this.catalog &&
                (this.catalog.displayMode === "catalog" ||
                    this.catalog.displayMode === "mix")
            ) {
                actions.push({
                    key: "search",
                    icon: SearchIcon,
                    label: this.showSearchBar
                        ? "Desactivar búsqueda"
                        : "Activar búsqueda",
                    colorClass: this.showSearchBar
                        ? "border-transparent bg-theme-success text-white hover:opacity-90"
                        : "border-transparent bg-theme-danger text-white hover:opacity-90",
                    onClick: this.toggleSearchBar
                });
            }

            if (this.$ability.hasPermission("catalog:GetHistory")) {
                actions.push({
                    key: "history",
                    icon: ClockIcon,
                    label: "Historial de versiones",
                    disabled: this.hasPendingChanges,
                    disabledReason:
                        "Guarda o descarta tus cambios para ver el historial",
                    onClick: () => {
                        this.historyModalOpen = true;
                    }
                });
            }

            return actions;
        },
        createCatalogUrl() {
            return MARKETPLACE_PORTAL_URL;
        },
        cardShapeStyle() {
            return getCardShapeStyle(DEFAULT_CARD_SHAPE, this.stack);
        },
        displayName() {
            if (this.draft?.businessName) return this.draft.businessName;
            if (this.tenantName) return this.tenantName;
            if (!this.tenantSlug) return "Catálogo";
            return this.tenantSlug
                .split("-")
                .filter(Boolean)
                .map((word) => word[0].toUpperCase() + word.slice(1))
                .join(" ");
        },
        socialEntries() {
            return this.socialNetworks
                .filter((entry) => Boolean(entry?.url))
                .map((entry) => ({
                    network: entry.network,
                    url: entry.url,
                    label: SOCIAL_LABELS[entry.network] ?? entry.network
                }));
        },
        customLinks() {
            const links = this.draft?.customLinks ?? [];
            return [...links].sort((a, b) => a.weight - b.weight);
        },
        isCustomLinksLimitReached() {
            return this.customLinks.length >= MAX_CUSTOM_LINKS;
        },
        hasPendingChanges() {
            if (!this.draft || !this.baseline) return false;
            return JSON.stringify(this.draft) !== JSON.stringify(this.baseline);
        },
        stack() {
            const merged = { ...DEFAULT_STACK };
            for (const key of Object.keys(DEFAULT_STACK)) {
                if (isHexColor(this.stackColors[key])) {
                    merged[key] = this.stackColors[key];
                }
            }
            return merged;
        },
        dark() {
            return luminance(this.stack.background) < 0.42;
        },
        // Letra sobre `bg-stack-emphasis` (misma definicion que en catalog y
        // en buildStackTheme); sobre el fondo se usa `text-stack-text`.
        ink() {
            return luminance(this.stack.emphasis) > 0.6 ? "#141210" : "#FFFFFF";
        },
        colors() {
            const { background } = this.stack;
            return {
                background,
                ink: this.ink,
                textSub: mix(
                    this.stack.text,
                    background,
                    this.dark ? 0.78 : 0.72
                ),
                line: alpha(this.stack.text, this.dark ? 0.16 : 0.1),
                tileBg: this.dark
                    ? alpha("#FFFFFF", 0.08)
                    : mix(this.stack.text, background, 0.06)
            };
        },
        pageBackground() {
            return mix(this.stack.background, "#000000", 0.45);
        },
        products: {
            get() {
                return (this.draft?.products ?? []).filter(
                    (product) => product.active
                );
            },
            set(reorderedProducts) {
                const rawProducts = this.draft.products;
                const orderedPublished = reorderedProducts.map((product) =>
                    rawProducts.find((p) => p.slug === product.slug)
                );
                let cursor = 0;
                this.draft.products = rawProducts.map((product) =>
                    product.active ? orderedPublished[cursor++] : product
                );
            }
        },
        unpublishedProducts() {
            return (this.draft?.products ?? []).filter(
                (product) => !product.active
            );
        },
        visibleProducts() {
            const term = this.search.trim().toLowerCase();
            if (!term) return this.products;
            return this.products.filter((product) =>
                product.name.toLowerCase().includes(term)
            );
        },
        visibleUnpublishedProducts() {
            const term = this.search.trim().toLowerCase();
            if (!term) return this.unpublishedProducts;
            return this.unpublishedProducts.filter((product) =>
                product.name.toLowerCase().includes(term)
            );
        },
        whatsappBase() {
            const number = this.whatsappNumber;
            if (number) return buildWhatsappWaMeLink(number);
            return (
                this.socialNetworks.find(
                    (entry) => entry.network === "whatsapp"
                )?.url ?? ""
            );
        },
        whatsappLink() {
            if (!this.whatsappBase) return "";
            return this.whatsappLinkFor(
                `Hola ${this.displayName}! Vi tu catálogo y quiero más información.`
            );
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("page:GetCatalog", "/app"))) return;
        await this.loadCatalog();
    },
    methods: {
        ...mapMutations("control", ["SET_TENANT_STACK_COLORS"]),
        ...mapActions("tenants", [
            "getCatalogPreview",
            "updateTenantBrandingSettings",
            "updateCatalogDesign",
            "updateTenantSegments",
            "updateWatermarkSettings"
        ]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openWatermarkUpgrade() {
            this.openPlanUpgrade({
                kind: "feature",
                feature: "catalogHideWatermark"
            });
        },
        // Se guarda al instante, como la vista del catálogo: no entra al
        // borrador de "Guardar cambios".
        async onWatermarkToggle(visible) {
            this.isSavingWatermark = true;
            try {
                await this.updateWatermarkSettings({
                    catalogHideWatermark: !visible
                });
                this.catalog.showWatermark = visible;
                this.isSavingWatermark = false;
            } catch (error) {
                this.isSavingWatermark = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        ...mapActions("customLinks", [
            "listCustomLinks",
            "updateCustomLinksWeights",
            "createCustomLink",
            "updateCustomLink",
            "deleteCustomLink"
        ]),
        ...mapActions("products", [
            "listProductsPaginated",
            "publishProduct",
            "updateProductsWeights"
        ]),
        goToHome() {
            this.$router.push({ name: "AppIndex" });
        },
        buildDraftFrom(catalogData, customLinks) {
            return {
                businessName: catalogData.businessName ?? "",
                businessDescription: catalogData.businessDescription ?? "",
                logoIconUrl: catalogData.logoIconUrl ?? "",
                socialNetworks: [...(catalogData.socialNetworks ?? [])],
                whatsappNumber: catalogData.whatsappNumber ?? "",
                // GET /bo/catalog no esta en el contrato de defaultCountry:
                // si no viene, "CO" (solo afecta la bandera de un +1).
                defaultCountry: normalizeWhatsappCountryCode(
                    catalogData.defaultCountry
                ),
                showSearchBar: Boolean(catalogData.showSearchBar),
                customLinks: [...customLinks],
                // Se pobla aparte via loadProducts() (GET /bo/products/all),
                // no viene incluido en getCatalogPreview.
                products: [],
                segments: [...(catalogData.segments ?? [])]
            };
        },
        async loadCustomLinks() {
            const links = await this.listCustomLinks({ type: "link" });
            return links
                .map((link) => ({
                    id: link.id,
                    title: link.title,
                    url: link.url,
                    weight: link.weight,
                    imageUrl: link.image?.url ?? null
                }))
                .sort((a, b) => a.weight - b.weight);
        },
        async loadCatalog() {
            try {
                await this.$loading.wrap(async () => {
                    const [catalogData, customLinks] = await Promise.all([
                        this.getCatalogPreview(),
                        this.loadCustomLinks()
                    ]);

                    this.catalog = catalogData;
                    this.SET_TENANT_STACK_COLORS(catalogData.stackColors);
                    const built = this.buildDraftFrom(catalogData, customLinks);
                    this.draft = built;
                    this.baseline = JSON.parse(JSON.stringify(built));
                });
            } catch (error) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                return;
            }
            // No entra al $loading.wrap de arriba: el resto del editor
            // (header, branding, redes, segments) ya puede mostrarse sin
            // esperar a los productos — la sección de productos muestra su
            // propio loader (isLoadingProducts) mientras esto resuelve.
            if (this.hasProductsDisplayMode) {
                this.loadProducts();
            }
        },
        async loadProducts() {
            this.isLoadingProducts = true;
            try {
                await this.fetchProductsPage({ page: 1, replace: true });
                this.isLoadingProducts = false;
            } catch (error) {
                this.isLoadingProducts = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        // orderBy: "weight" (no el default "updatedAt" de GET /bo/products):
        // el reorder de abajo reasigna weight 1..N según el índice de
        // this.draft.products, así que cada página cargada debe respetar el
        // orden real del catálogo para que esa reasignación no lo reviente.
        // complete: true es obligatorio — sin eso el backend no arma
        // `pagination` (GetCatalogProductsBOByFilters solo la calcula si
        // complete es truthy) y hasMoreProducts quedaría siempre false.
        async fetchProductsPage({ page, replace }) {
            const { items, pagination } = await this.listProductsPaginated({
                page,
                itemsByPage: PRODUCTS_PAGE_SIZE,
                orderBy: "weight",
                orderType: "ASC",
                complete: true
            });
            this.productsPage = page;
            this.hasMoreProducts = Boolean(
                pagination && page < pagination.pagesNumber
            );
            if (replace) {
                this.draft.products = items;
                this.baseline.products = JSON.parse(JSON.stringify(items));
            } else {
                this.draft.products = [...this.draft.products, ...items];
                this.baseline.products = [
                    ...this.baseline.products,
                    ...JSON.parse(JSON.stringify(items))
                ];
            }
        },
        async loadMoreProducts() {
            if (!this.hasMoreProducts || this.isLoadingMoreProducts) return;
            this.isLoadingMoreProducts = true;
            try {
                await this.fetchProductsPage({
                    page: this.productsPage + 1,
                    replace: false
                });
                this.isLoadingMoreProducts = false;
            } catch (error) {
                this.isLoadingMoreProducts = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async onDisplayModeUpdated(displayMode) {
            this.catalog.displayMode = displayMode;
            // Al pasar a 'feed' o 'catalog' el backend rearma los segments
            // con todos los enlaces del tenant: sin recargar, el borrador
            // local seguiría con los bloques viejos y el próximo guardado
            // los reescribiría.
            if (LINKS_ONLY_DISPLAY_MODES.includes(displayMode)) {
                await this.loadCatalog();
                return;
            }
            // `mix`/`segments` no recargan el catálogo: los productos se
            // piden acá si la vista los muestra.
            if (this.hasProductsDisplayMode) {
                this.loadProducts();
            }
        },
        onLogoIconUpdated(url) {
            this.draft.logoIconUrl = url;
        },
        startEditingName() {
            this.nameDraft = this.displayName;
            this.nameModalOpen = true;

            this.$nextTick(() => {
                this.$refs.nameInput.focus();
            });
        },

        resetNameForm() {
            this.nameDraft = "";
        },

        finishEditingName() {
            const name = this.nameDraft.trim();
            if (!name) return;

            this.draft.businessName = name;
            this.nameModalOpen = false;
        },

        startEditing() {
            this.descriptionDraft = this.businessDescription;
            this.descriptionModalOpen = true;

            this.$nextTick(() => {
                this.$refs.descriptionInput.focus();
            });
        },

        resetDescriptionForm() {
            this.descriptionDraft = "";
        },

        finishEditing() {
            this.draft.businessDescription = this.descriptionDraft;
            this.descriptionModalOpen = false;
        },

        cancelEditing() {
            this.descriptionModalOpen = false;
        },

        startEditingWhatsapp() {
            const { country, localNumber } = splitWhatsappNumber(
                this.whatsappNumber,
                this.draftDefaultCountry
            );
            this.whatsappDraft = localNumber;
            this.whatsappDraftCountry = country;
            this.whatsappDraftTouched = false;
            this.whatsappModalOpen = true;

            this.$nextTick(() => {
                this.$refs.whatsappInput.focus();
            });
        },

        resetWhatsappForm() {
            this.whatsappDraft = "";
            this.whatsappDraftCountry = this.draftDefaultCountry;
            this.whatsappDraftTouched = false;
        },

        finishEditingWhatsapp() {
            this.whatsappDraftTouched = true;
            if (!this.isWhatsappDraftValid) return;

            this.draft.whatsappNumber = buildWhatsappNumber(
                this.whatsappDraftCountry,
                this.whatsappDraft
            );
            this.draft.defaultCountry = this.whatsappDraftCountry;
            this.whatsappModalOpen = false;
        },

        whatsappLinkFor(message) {
            const separator = this.whatsappBase.includes("?") ? "&" : "?";
            return `${this.whatsappBase}${separator}text=${encodeURIComponent(
                message
            )}`;
        },
        openSocialModal() {
            this.newSocialNetworksList = this.socialNetworks.map((entry) => ({
                key: entry.network,
                url: entry.url
            }));
            this.socialModalOpen = true;
        },
        resetSocialForm() {
            this.newSocialNetworksList = [];
        },
        openThemeModal() {
            this.themeForm = {
                theme: this.catalog?.theme ?? "",
                stackColors: { ...this.stack }
            };
            this.themeModalOpen = true;
        },
        resetThemeForm() {
            this.themeForm = { stackColors: { ...DEFAULT_STACK } };
        },
        async updateTheme() {
            this.isThemeSaving = true;
            try {
                await this.updateCatalogDesign({
                    stackColors: { ...this.themeForm.stackColors }
                });
                this.catalog.stackColors = { ...this.themeForm.stackColors };
                this.SET_TENANT_STACK_COLORS(this.catalog.stackColors);
                this.isThemeSaving = false;
                this.themeModalOpen = false;
                this.$sound.play("success");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Apariencia actualizada",
                    text: "Los colores de tu negocio han sido guardados.",
                    icon: "success"
                });
            } catch (error) {
                this.isThemeSaving = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        toggleSearchBar() {
            this.draft.showSearchBar = !this.draft.showSearchBar;
        },
        toggleLinksReorder() {
            this.linksReorderMode = !this.linksReorderMode;
            if (this.linksReorderMode) {
                this.reorderedLinks = [...this.customLinks];
            }
        },
        onLinksDragDrop(dropResult) {
            this.reorderedLinks = applyDrag(
                [...this.reorderedLinks],
                dropResult
            );
        },
        saveLinksReorder() {
            this.draft.customLinks = this.reorderedLinks.map((link, index) => ({
                ...link,
                weight: index + 1
            }));
            this.linksReorderMode = false;
        },
        removeLink(link) {
            this.draft.customLinks = this.draft.customLinks.filter(
                (item) => item.id !== link.id
            );
        },
        openLinkModal() {
            this.newLinkForm = { title: "", url: "", image: null };
            this.linkModalErrors = {};
            this.linkModalOpen = true;
        },
        resetLinkForm() {
            this.newLinkForm = { title: "", url: "", image: null };
            this.linkModalErrors = {};
        },
        validateNewLinkForm() {
            this.linkModalErrors = {};
            if (!this.newLinkForm.title.trim()) {
                this.linkModalErrors.title = "El título es obligatorio";
            }
            if (!this.newLinkForm.url.trim()) {
                this.linkModalErrors.url = "La URL es obligatoria";
            }
            return Object.keys(this.linkModalErrors).length === 0;
        },
        async saveNewLink() {
            if (!this.validateNewLinkForm() || this.isSavingLink) return;

            this.isSavingLink = true;
            try {
                const payload = {
                    title: this.newLinkForm.title.trim(),
                    url: this.newLinkForm.url.trim()
                };
                if (this.newLinkForm.image) {
                    payload.imageId = this.newLinkForm.image.id;
                }
                const created = await this.createCustomLink(payload);
                this.draft.customLinks = [
                    ...this.draft.customLinks,
                    {
                        id: created.id,
                        title: created.title,
                        url: created.url,
                        weight: created.weight,
                        imageUrl: created.image?.url ?? null
                    }
                ];
                this.isSavingLink = false;
                this.linkModalOpen = false;
                this.$sound.play("success");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Enlace creado",
                    text: "El enlace ya aparece en tu catálogo.",
                    icon: "success"
                });
            } catch (error) {
                this.isSavingLink = false;
                if (this.openPlanUpgradeForLimit(error)) {
                    this.linkModalOpen = false;
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        openProductModal() {
            this.productModalOpen = true;
        },
        onProductCreatedFromEntry(product) {
            if (!product) return;
            this.draft.products = [...this.draft.products, product];
        },
        editProduct(product) {
            window.open(`/app/products/detail/${product.id}`, "_blank");
        },
        toggleProductsReorder() {
            this.productsReorderMode = !this.productsReorderMode;
        },
        togglePublish(product) {
            this.draft.products = this.draft.products.map((item) =>
                item.id === product.id
                    ? { ...item, active: !product.active }
                    : item
            );
        },
        saveSocialNetworks() {
            this.draft.socialNetworks = this.newSocialNetworksList
                .map((item) => ({
                    network: item.key,
                    url: (item.url || "").trim()
                }))
                .filter((item) => item.url);

            this.socialModalOpen = false;
        },
        diffBranding() {
            const fields = [
                "businessName",
                "businessDescription",
                "logoIconUrl",
                "socialNetworks",
                "whatsappNumber",
                "defaultCountry"
            ];
            const changedFields = fields.filter(
                (field) =>
                    JSON.stringify(this.draft[field]) !==
                    JSON.stringify(this.baseline[field])
            );
            if (!changedFields.length) return null;

            const payload = {};
            if (changedFields.includes("businessName")) {
                payload.name = this.draft.businessName;
            }
            if (changedFields.includes("businessDescription")) {
                payload.businessDescription = this.draft.businessDescription;
            }
            if (changedFields.includes("logoIconUrl")) {
                payload.logoIconUrl = this.draft.logoIconUrl;
            }
            if (changedFields.includes("socialNetworks")) {
                payload.socialNetworks = this.draft.socialNetworks;
            }
            if (
                changedFields.includes("whatsappNumber") ||
                changedFields.includes("defaultCountry")
            ) {
                payload.whatsappNumber = this.draft.whatsappNumber;
                payload.defaultCountry = this.draft.defaultCountry;
            }
            return { payload, changedFields };
        },
        diffCatalogDesign() {
            const fields = ["showSearchBar"];
            const changedFields = fields.filter(
                (field) =>
                    JSON.stringify(this.draft[field]) !==
                    JSON.stringify(this.baseline[field])
            );
            if (!changedFields.length) return null;

            const payload = {};
            if (changedFields.includes("showSearchBar")) {
                payload.showSearchBar = this.draft.showSearchBar;
            }
            return { payload, changedFields };
        },
        diffCustomLinks() {
            const draftLinks = this.draft.customLinks;
            const baselineLinks = this.baseline.customLinks;
            const draftIds = new Set(draftLinks.map((link) => link.id));

            const deletedIds = baselineLinks
                .filter((link) => !draftIds.has(link.id))
                .map((link) => link.id);

            const updated = draftLinks.filter((link) => {
                const original = baselineLinks.find(
                    (item) => item.id === link.id
                );
                return (
                    original &&
                    (original.title !== link.title ||
                        original.url !== link.url ||
                        original.imageUrl !== link.imageUrl)
                );
            });

            const remainingBaselineOrder = baselineLinks
                .filter((link) => draftIds.has(link.id))
                .map((link) => link.id);
            const draftOrder = draftLinks.map((link) => link.id);
            const orderChanged =
                JSON.stringify(remainingBaselineOrder) !==
                JSON.stringify(draftOrder);

            return { deletedIds, updated, orderChanged };
        },
        diffProducts() {
            const draftProducts = this.draft.products;
            const baselineProducts = this.baseline.products;

            const publishChanged = draftProducts.filter((product) => {
                const original = baselineProducts.find(
                    (item) => item.id === product.id
                );
                return original && original.active !== product.active;
            });

            const draftPublishedOrder = draftProducts
                .filter((product) => product.active)
                .map((product) => product.id);
            const baselinePublishedOrder = baselineProducts
                .filter((product) => product.active)
                .map((product) => product.id);
            const weightsChanged =
                JSON.stringify(draftPublishedOrder) !==
                JSON.stringify(baselinePublishedOrder);

            return { publishChanged, weightsChanged };
        },
        toStoredSegment(segment) {
            if (segment.type === "links" || segment.type === "banners") {
                return {
                    id: segment.id,
                    type: segment.type,
                    style: segment.style,
                    linkIds: (segment.links || []).map((link) => link.id)
                };
            }
            if (segment.type === "productsCarousel") {
                return {
                    id: segment.id,
                    type: segment.type,
                    style: segment.style,
                    productIds: (segment.products || []).map(
                        (product) => product.id
                    )
                };
            }
            if (segment.type === "text") {
                const base = {
                    id: segment.id,
                    type: "text",
                    style: segment.style,
                    title: segment.title || undefined,
                    fontFamily: segment.fontFamily || undefined
                };
                if (segment.style === "titleImage") {
                    return {
                        ...base,
                        text: segment.text || undefined,
                        imageId: segment.image?.id ?? segment.imageId
                    };
                }
                return { ...base, text: segment.text || undefined };
            }
            if (segment.type === "testimonials") {
                return {
                    id: segment.id,
                    type: "testimonials",
                    style: segment.style,
                    items: (segment.items || []).map((item) => ({
                        id: item.id,
                        name: item.name,
                        quote: item.quote,
                        imageId: item.image?.id ?? item.imageId ?? undefined
                    }))
                };
            }
            return segment;
        },
        discardDraftChanges() {
            this.draft = JSON.parse(JSON.stringify(this.baseline));
        },
        async saveDraftChanges() {
            if (!this.hasPendingChanges || this.isSavingDraft) return;
            this.isSavingDraft = true;

            const tasks = [];

            const brandingDiff = this.diffBranding();
            if (brandingDiff) {
                tasks.push(
                    this.updateTenantBrandingSettings(
                        brandingDiff.payload
                    ).then(() => {
                        for (const field of brandingDiff.changedFields) {
                            this.baseline[field] = JSON.parse(
                                JSON.stringify(this.draft[field])
                            );
                        }
                    })
                );
            }

            const designDiff = this.diffCatalogDesign();
            if (designDiff) {
                tasks.push(
                    this.updateCatalogDesign(designDiff.payload).then(() => {
                        for (const field of designDiff.changedFields) {
                            this.baseline[field] = JSON.parse(
                                JSON.stringify(this.draft[field])
                            );
                        }
                    })
                );
            }

            const linksDiff = this.diffCustomLinks();
            for (const link of linksDiff.updated) {
                tasks.push(
                    this.updateCustomLink({
                        id: link.id,
                        title: link.title,
                        url: link.url
                    }).then(() => {
                        const index = this.baseline.customLinks.findIndex(
                            (item) => item.id === link.id
                        );
                        if (index !== -1) {
                            this.baseline.customLinks.splice(
                                index,
                                1,
                                JSON.parse(JSON.stringify(link))
                            );
                        }
                    })
                );
            }
            for (const id of linksDiff.deletedIds) {
                tasks.push(
                    this.deleteCustomLink({ id }).then(() => {
                        this.baseline.customLinks =
                            this.baseline.customLinks.filter(
                                (item) => item.id !== id
                            );
                    })
                );
            }
            if (linksDiff.orderChanged) {
                tasks.push(
                    this.updateCustomLinksWeights({
                        customLinks: this.draft.customLinks.map(
                            (link, index) => ({
                                id: link.id,
                                weight: index + 1
                            })
                        )
                    }).then(() => {
                        this.baseline.customLinks = JSON.parse(
                            JSON.stringify(this.draft.customLinks)
                        );
                    })
                );
            }

            // Free no edita bloques: sin el permiso no se manda nada (el
            // backend respondía 403 y frenaba el resto del guardado).
            const segmentsChanged =
                this.$ability.hasPermission("catalog:UpdateSegments") &&
                JSON.stringify(this.draft.segments) !==
                    JSON.stringify(this.baseline.segments);
            if (segmentsChanged) {
                tasks.push(
                    this.updateTenantSegments({
                        segments: this.draft.segments.map((segment) =>
                            this.toStoredSegment(segment)
                        )
                    }).then(() => {
                        this.baseline.segments = JSON.parse(
                            JSON.stringify(this.draft.segments)
                        );
                    })
                );
            }

            const productsDiff = this.diffProducts();
            if (productsDiff.weightsChanged) {
                tasks.push(
                    this.updateProductsWeights({
                        products: this.draft.products
                            .filter((product) => product.active)
                            .map((product, index) => ({
                                id: product.id,
                                weight: index + 1
                            }))
                    }).then(() => {
                        this.baseline.products = JSON.parse(
                            JSON.stringify(this.draft.products)
                        );
                    })
                );
            }
            for (const product of productsDiff.publishChanged) {
                tasks.push(
                    this.publishProduct({
                        active: product.active,
                        id: product.id,
                        weight: product.weight
                    }).then(() => {
                        const index = this.baseline.products.findIndex(
                            (item) => item.id === product.id
                        );
                        if (index !== -1) {
                            this.baseline.products.splice(
                                index,
                                1,
                                JSON.parse(JSON.stringify(product))
                            );
                        }
                    })
                );
            }

            const results = await Promise.allSettled(tasks);
            const failed = results.filter(
                (result) => result.status === "rejected"
            );

            this.isSavingDraft = false;

            // Guardar más bloques de los que permite el plan: modal "Pásate a…"
            // en vez del aviso genérico (el resto de cambios sí se guardó).
            const reasons = failed.map((result) => result.reason);
            const limitError = reasons.find((reason) =>
                isPlanLimitError(reason)
            );
            if (limitError) {
                this.openPlanUpgradeForLimit(limitError);
                if (reasons.every((reason) => isPlanLimitError(reason))) {
                    return;
                }
            }

            if (failed.length) {
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Algunos cambios no se guardaron",
                    text: "Revisa e intenta guardar de nuevo.",
                    icon: "error"
                });
                return;
            }

            await this.loadCatalog();
            this.$sound.play("success");
            await this.$swal.fire({
                background: this.backgroundColor,
                title: "Cambios guardados",
                text: "Tu catálogo ha sido actualizado.",
                icon: "success"
            });
        }
    }
};
</script>
